import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SrIcon } from "@/components/ui/SrIcon";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { EquipmentFlagGate } from "@/components/equipment/EquipmentFlagGate";
import { ALL_DEPARTMENTS, DepartmentToggle } from "@/components/equipment/DepartmentToggle";
import { ReportDamageDialog } from "@/components/equipment/ReportDamageDialog";
import { ServiceTable } from "@/components/equipment/ServiceTable";
import { ServiceTicketDialog } from "@/components/equipment/ServiceTicketDialog";
import { useDepartments } from "@/hooks/use-departments";
import { useEquipmentAccess, useEquipmentList } from "@/hooks/use-equipment";
import { useServiceTickets } from "@/hooks/use-equipment-service";
import type { DBEquipmentServiceTicket } from "@/lib/db-types";

type View = "active" | "resolved" | "all";

/**
 * /utstyr/service (VIB-2921, skjerm I). Alle interne ser sakene og kan melde
 * skade; admin og utstyrsansvarlige endrer status og verksted, og bare admin
 * ser og setter kostnad.
 */
export default function EquipmentServicePage() {
  return (
    <EquipmentFlagGate>
      <EquipmentService />
    </EquipmentFlagGate>
  );
}

function EquipmentService() {
  const access = useEquipmentAccess();
  const { data: tickets = [], isLoading } = useServiceTickets();
  const { data: departments = [] } = useDepartments();
  const [reportOpen, setReportOpen] = useState(false);
  const { data: items = [] } = useEquipmentList(access.canManage, reportOpen && !access.isLoading);
  const [view, setView] = useState<View>("active");
  const [department, setDepartment] = useState(ALL_DEPARTMENTS);
  const [editing, setEditing] = useState<DBEquipmentServiceTicket | null>(null);

  const scoped = useMemo(
    () => tickets.filter((t) => department === ALL_DEPARTMENTS || t.item_department_id === department),
    [tickets, department],
  );
  const activeCount = scoped.filter((t) => t.status !== "resolved").length;
  const rows = scoped.filter((t) => view === "all" || (view === "active" ? t.status !== "resolved" : t.status === "resolved"));
  const reportable = useMemo(
    () => items.filter((i) => i.status !== "sold" && i.status !== "retired").map((i) => ({ id: i.id, name: i.name })),
    [items],
  );

  if (isLoading || access.isLoading) {
    return (
      <div className="page-container flex justify-center py-12">
        <SrIcon name="loader2" className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="page-container animate-fade-in space-y-6">
      <nav aria-label="Brødsmuler" className="flex gap-1.5 text-sm text-muted-foreground">
        <Link to="/utstyr" className="hover:underline">Utstyr</Link>
        <span>/</span>
        <span>Service</span>
      </nav>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display font-semibold text-[clamp(28px,3vw,40px)] tracking-[-0.01em] flex items-center gap-2">
          <SrIcon name="wrench" className="h-6 w-6" />
          Service
        </h1>
        <Button onClick={() => setReportOpen(true)}>
          <SrIcon name="plus" className="h-4 w-4" />
          Meld skade
        </Button>
      </div>
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <ToggleGroup type="single" variant="outline" size="sm" value={view} onValueChange={(v) => v && setView(v as View)} aria-label="Visning">
              <ToggleGroupItem value="active">Åpne · {activeCount}</ToggleGroupItem>
              <ToggleGroupItem value="resolved">Ferdige</ToggleGroupItem>
              <ToggleGroupItem value="all">Alle</ToggleGroupItem>
            </ToggleGroup>
            <DepartmentToggle departments={departments} value={department} onChange={setDepartment} />
          </div>
          <ServiceTable tickets={rows} showCost={access.isAdmin} onEdit={access.canManage ? setEditing : undefined} />
          <p className="text-xs text-muted-foreground">
            «Ikke brukbar» setter utstyret «På service», og det kan ikke bookes før saken er ferdig. Utstyrsansvarlig får varsel i Revehiet.
          </p>
        </CardContent>
      </Card>
      <ReportDamageDialog open={reportOpen} onOpenChange={setReportOpen} item={null} items={reportable} />
      <ServiceTicketDialog ticket={editing} onOpenChange={(open) => !open && setEditing(null)} canSetCost={access.isAdmin} />
    </div>
  );
}
